// API layer for the contractor compliance portal — RON-5465.
//
// THE ONE THING THAT MAKES THIS FILE DIFFERENT FROM EVERY OTHER API LAYER HERE: these endpoints
// have no Ronspot login. The caller is a contractor — somebody outside the customer's company,
// with no account and nothing to authenticate with. The pack email is how they got here.
//
// So this must NEVER use the app's shared `post()`: that attaches the signed-in user's bearer
// token, and this page is open to the internet. Sessions here are an `X-Portal-Token` from
// `verify-code`, held in memory for the life of the tab and nowhere else.
//
// While `USE_PORTAL_MOCKS` is true the fixtures answer instead. They are in WIRE FORMAT, so the
// mapper does the same work on both paths.

// URLs and the base only — NOT `post()`. See the network section below for why that matters here
// more than anywhere else in the app.
import {
  BASE_URL,
  PortalDocumentSign_url,
  PortalDocumentUploadUrl_url,
  PortalDocumentUploaded_url,
  PortalDocument_url,
  PortalPack_url,
  PortalPersonnelConfirm_url,
  PortalOpen_url,
  PortalRequestCode_url,
  PortalSignOut_url,
  PortalSubmit_url,
  PortalVerifyCode_url,
} from "../../config/ApiConfig.js";
import {
  MOCK_CLOSED_LINK,
  MOCK_CODE,
  MOCK_LINK,
  MOCK_LOGO_URL,
  MOCK_MASKED_EMAIL,
  MOCK_LINK_REFERENCE,
  linkStateForToken,
  MOCK_DOCUMENT_BODY,
  MOCK_PACK,
  USE_PORTAL_MOCKS,
} from "./portalMockData.js";
import {
  documentBodyFromApi,
  packFromApi,
  requestCodeFromApi,
  signToApi,
  uploadUrlToApi,
  uploadedToApi,
  openFromApi,
  verifyCodeFromApi,
} from "./portalApiMapper.js";

const MOCK_LATENCY_MS = 260;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The same envelope the rest of the module uses, so one response handler serves every screen.
const mockResponse = (records) => ({
  Data: { Message: "", ResponseCode: 200, ErrorMessage: "", Records: records },
});

const mockFailure = (code, message) => ({
  Data: { Message: "fail", ResponseCode: code, ErrorMessage: message, Records: {} },
});

// §1: the envelope is not the HTTP status. Only 401 is a real one; everything else arrives as
// HTTP 200 with the outcome inside.
export const isSuccessfulResponse = (response) => {
  const code = Number(response?.Data?.ResponseCode);

  return code >= 200 && code < 300;
};

// ── The session ──────────────────────────────────────────────────────────────────
//
// IN MEMORY, DELIBERATELY. Not localStorage, not a cookie.
//
// This is a device that may be shared — a contractor's phone passed around a van, a machine in a
// site office. A token in storage outlives the tab and the person, and this one opens somebody's
// compliance pack. 60 minutes is the server's limit; the tab closing should be ours.
let portalToken = null;

export const setPortalToken = (token) => {
  portalToken = token || null;
};

export const getPortalToken = () => portalToken;

export const clearPortalToken = () => {
  portalToken = null;
};

// The LINK token from the route — renamed from `portalGuid` on 22-October-2026.
//
// TWO DIFFERENT TOKENS live in this file and confusing them would be a security bug, so they are
// named apart:
//
//   · `linkToken`   — from the page's own URL. Says WHICH pack. Arrives by email.
//   · `portalToken` — above. The SESSION, issued by `verify-code`. Proves WHO.
//
// Held here rather than threaded through every call, because only the sign-in endpoints take it
// and all of them are called from one screen.
//
// It is not a credential on its own: the emailed code is what proves identity. But unlike the
// guid it replaced, it DOES expire — 30 days by default — and a newer link closes it, along with
// any session opened through it (contract §1, "The link").
let linkToken = null;

export const setLinkToken = (token) => {
  linkToken = token || null;
};

export const getLinkToken = () => linkToken;

// ── Talking to the network ───────────────────────────────────────────────────────
//
// ITS OWN FETCH, AND THAT IS THE WHOLE POINT OF THIS SECTION.
//
// Every other API layer in this repo calls `post()` from `src/api/HttpClient.js`, which attaches
// the signed-in employee's bearer token. This page is open to the internet to anyone holding a
// link, so doing that here would hand an employee's credentials to a stranger's browser. There is
// a test asserting the import is absent.
//
// The session goes in `X-Portal-Token`. NOT `Authorization` — the walkthrough lists "403 on every
// call after signing in" against exactly that mistake.

const jsonHeaders = () => {
  const headers = { "Content-Type": "application/json", Accept: "application/json" };

  if (portalToken) headers["X-Portal-Token"] = portalToken;

  return headers;
};

// Everything the app treats as a response, including the failures — so one handler serves every
// call and a screen never has to tell a thrown error from a refusal.
const envelope = (code, message, records = {}) => ({
  Data: {
    Message: code >= 200 && code < 300 ? "" : "fail",
    ResponseCode: code,
    ErrorMessage: code >= 200 && code < 300 ? "" : message,
    Records: records,
  },
});

const request = async (url, { method = "POST", body } = {}) => {
  let response;

  try {
    response = await fetch(`${BASE_URL}${url}`, {
      method,
      headers: jsonHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    // The network never answered. Said in the contractor's terms, because they are on a phone on
    // a building site as often as not, and "TypeError: Failed to fetch" tells them nothing.
    return envelope(0, "We could not reach Ronspot. Check your connection and try again.");
  }

  let payload = null;

  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  // §1: the outcome lives in the envelope, not the HTTP status — 403, 404, 409 and 422 all arrive
  // as HTTP 200. So a well-formed envelope is returned untouched whatever the status was.
  if (payload?.Data && payload.Data.ResponseCode !== undefined) return payload;

  // No envelope. A 401 is the one real HTTP status, and on THIS module it means the session is
  // gone — there is no refresh and nobody to log in, so the screen sends them back for a new code.
  //
  // THE WORDING COVERS TWO CASES, on the Laravel team's advice (22-October-2026). A session can
  // end because 60 minutes passed, OR because a NEWER LINK was issued — a reminder, a resend or
  // a rejection email — which closes the old link and every session opened through it.
  //
  // The server answers both with the same 403 deliberately: telling them apart would help
  // somebody probing. So rather than guess which happened, the sentence covers both, and the
  // contractor always has the newer email in hand if there is one.
  if (response.status === 401 || response.status === 403) {
    return envelope(
      403,
      "Your session has ended. If you have received a newer email from us, use the link in it; otherwise request a new code."
    );
  }

  return envelope(
    response.status || 0,
    "Something went wrong. Please try again, or ask for a new code."
  );
};

// ── The mock server's state ──────────────────────────────────────────────────────
//
// A copy, so `portalMockData.js` stays a pure fixture and a reload resets everything. Signing and
// uploading MUST persist for the length of the session or the flow cannot be reviewed: a document
// that reads "not signed" a second after signing it looks broken.
let packStore = null;

const clonePack = (pack) => JSON.parse(JSON.stringify(pack));

const store = () => {
  if (packStore === null) packStore = clonePack(MOCK_PACK);

  return packStore;
};

// Reset between tests, and when the tab signs out.
export const resetMockPack = () => {
  packStore = null;
};

const allItems = () => [
  ...store().steps.bank_documents.items,
  ...store().steps.my_documents.items,
];

const findDocument = (id) => allItems().find((item) => item.id === id) || null;

// Recount everything the screen reads from a count rather than from the list. The real server does
// this on every write — §4: "every write returns the whole pack" — and the mock has to, or step 4
// drifts from steps 1 and 2 and the bug the contract designed out reappears here.
const recount = () => {
  const pack = store();

  [pack.steps.bank_documents, pack.steps.my_documents].forEach((step) => {
    step.total = step.items.length;
    step.outstanding = step.items.filter((item) => item.outstanding).length;
  });

  const outstanding = [];

  pack.steps.bank_documents.items
    .filter((item) => item.outstanding)
    .forEach((item) =>
      outstanding.push({ id: item.id, title: item.title, step: 1, why: "Not signed yet." })
    );

  pack.steps.my_documents.items
    .filter((item) => item.outstanding)
    .forEach((item) =>
      outstanding.push({
        id: item.id,
        title: item.title,
        step: 2,
        // A rejection carries the reviewer's own sentence; anything else here is simply not sent.
        why: item.state === "rejected" ? item.rejected_reason : "Not uploaded yet.",
      })
    );

  if (pack.steps.personnel.required && !pack.steps.personnel.confirmed) {
    outstanding.push({ id: null, title: "Confirm your personnel", step: 3, why: "Not confirmed yet." });
  }

  pack.steps.submit.outstanding = outstanding;
  pack.steps.submit.can_submit = outstanding.length === 0;

  return pack;
};

// Every write answers with the whole pack, exactly as the real endpoints do.
// `message` is the server's own sentence, shown verbatim by the screen. Empty for most writes;
// the `uploaded` reply uses it to carry AC-P-66's "Sent for review."
const packResponse = (message = "") => ({
  Data: { Message: message, ResponseCode: 200, ErrorMessage: "", Records: clonePack(recount()) },
});

// ── Sign-in ──────────────────────────────────────────────────────────────────────

// `POST /visitor/portal/open` — Phase A, THE FIRST CALL THE PAGE MAKES.
//
// Nothing else can be drawn until this answers: a live link gets the code screen, a closed one
// gets the expired screen and nothing more, and an unknown token gets a flat refusal.
//
// It is also the only place a closed link can be told apart from a live one BEFORE a code is
// requested — `request-code` answers identically for both, deliberately.
export const OpenPortal_api = async () => {
  if (!USE_PORTAL_MOCKS) {
    return request(PortalOpen_url, { method: "POST", body: { token: linkToken } });
  }

  await delay(MOCK_LATENCY_MS);

  // The fixture serves whichever state `MOCK_LINK_STATE` names, so all three screens can be
  // walked through without a server. A real token is 43 characters and carries no ids.
  const linkState = linkStateForToken(linkToken);

  if (linkState === "missing") {
    return mockFailure(404, "This link is not valid.");
  }

  if (linkState === "expired") {
    return mockResponse(MOCK_CLOSED_LINK);
  }

  return mockResponse({
    state: "valid",
    company: store().customer,
    link: MOCK_LINK,
    // The four granted on 24-October (answers-to-frontend-21). All additive, all on `open` only.
    logo_url: MOCK_LOGO_URL,
    masked_email: MOCK_MASKED_EMAIL,
    contact: MOCK_CLOSED_LINK.contact,
    reference: MOCK_LINK_REFERENCE,
  });
};

// `POST /visitor/portal/request-code` — the token in the BODY, not the path (Phase A).
//
// Answers IDENTICALLY for a real pack, an unknown token, a closed link and a finished one (§1).
// The mock keeps that property on purpose: a screen built against a mock that distinguishes them
// would grow a branch the real API can never satisfy.
export const RequestPortalCode_api = async () => {
  if (!USE_PORTAL_MOCKS) {
    return request(PortalRequestCode_url, { method: "POST", body: { token: linkToken } });
  }

  await delay(MOCK_LATENCY_MS);

  // `{ sent: true }` and nothing else, exactly as §1 specifies. The mock used to hand back a
  // masked address the real endpoint has never sent, which let the verify screen be built on a
  // field that would be absent in production.
  return mockResponse({ sent: true });
};

// `POST /visitor/portal/verify-code` — the token in the BODY alongside the code (Phase A).
export const VerifyPortalCode_api = async (requestParams = {}) => {
  if (!USE_PORTAL_MOCKS) {
    // The six digits, exactly as §2 wants them. The 422 for a malformed code is the server's to
    // give, and it is checked before anything is looked up — so a typo does not burn an attempt.
    return request(PortalVerifyCode_url, {
      method: "POST",
      body: { token: linkToken, code: String(requestParams.Code ?? "").trim() },
    });
  }

  await delay(MOCK_LATENCY_MS);

  const code = String(requestParams.Code ?? "").trim();

  if (!/^\d{6}$/.test(code)) {
    return mockFailure(422, "Enter the 6-digit code from your email.");
  }

  // The real one locks the box for 15 minutes after five wrong codes, counted in the database.
  // The mock refuses without the lockout — the point here is the screen's wrong-code state.
  if (code !== MOCK_CODE) {
    return mockFailure(403, "That code was not recognised. Check it and try again.");
  }

  // The same five fields §2 lists. `submitted` lets the screen draw the read-only view at once
  // rather than fetching the pack to discover it.
  return mockResponse({
    token: "mock-portal-session",
    expires_in: 3600,
    contractor_name: store().contractor.name,
    company: store().contractor.company,
    submitted: store().submitted === true,
    // Added by Phase A: the header pill and the expiry notice read this, and there is no second
    // call that would fetch it.
    link: MOCK_LINK,
  });
};

// ── Reads ────────────────────────────────────────────────────────────────────────

// `GET /visitor/portal/pack` — the whole screen in one call.
export const GetPortalPack_api = async () => {
  if (!USE_PORTAL_MOCKS) return request(PortalPack_url, { method: "GET" });

  await delay(MOCK_LATENCY_MS);

  return packResponse();
};

// REMOVED 10-October-2026: `GetPortalVisit_api`.
//
// It existed because the pack carried no visit and the banner had to come from somewhere. It was
// never a real endpoint — it answered from a fixture, and returned an empty success on the live
// path so the screen degraded instead of throwing.
//
// `customer` and `visit` now ride on `GET /pack` (shipped 09-October, answers-to-frontend-6),
// so the banner draws from the pack like everything else and there is one call, not two.
//
// The personnel CHIPS are still ours, and still invented: §8 is explicit that step 3 is "a tick,
// not a crew list". That one did not change.

// `GET /visitor/portal/documents/{id}` — one document, with the text to read.
export const GetPortalDocument_api = async (requestParams = {}) => {
  if (!USE_PORTAL_MOCKS) {
    return request(PortalDocument_url(requestParams.DocumentID), { method: "GET" });
  }

  await delay(MOCK_LATENCY_MS);

  const document = findDocument(requestParams.DocumentID);

  // §1: another pack's document answers 404, never 403 — a 403 would confirm it exists.
  if (!document) return mockFailure(404, "That document was not found.");

  return mockResponse({ id: document.id, title: document.title, body: MOCK_DOCUMENT_BODY });
};

// ── Writes ───────────────────────────────────────────────────────────────────────

// `POST /visitor/portal/documents/{id}/sign`
export const SignPortalDocument_api = async (requestParams = {}) => {
  if (!USE_PORTAL_MOCKS) {
    // All three of §5's fields, built by the mapper so the field names live in one place. The
    // reply is the WHOLE refreshed pack, not the document that changed.
    return request(PortalDocumentSign_url(requestParams.DocumentID), {
      method: "POST",
      body: signToApi(requestParams),
    });
  }

  await delay(MOCK_LATENCY_MS);

  const document = findDocument(requestParams.DocumentID);
  if (!document) return mockFailure(404, "That document was not found.");

  if (store().read_only) {
    return mockFailure(409, "This pack has been submitted and can no longer be changed.");
  }

  // The three refusals §5 lists, in the server's own words and the server's own order. The mock
  // enforces them so a screen that stops sending one of these fields fails HERE, in a test, rather
  // than on staging — which is exactly how the missing `consent` survived until the walk-through
  // was read.
  if (requestParams.Consent !== true) {
    return mockFailure(409, "Please tick the box to confirm you agree.");
  }

  if (document.read_to_end === true && requestParams.ReadToEnd !== true) {
    return mockFailure(409, "Please read the whole document before signing it.");
  }

  if (document.ack_method === "esign" && !String(requestParams.Name ?? "").trim()) {
    return mockFailure(409, "Please type your full name to sign.");
  }

  document.state = "signed";
  document.outstanding = false;
  document.signed_at = new Date().toISOString().slice(0, 19).replace("T", " ");
  document.signed_name = requestParams.Name || store().contractor.name;

  return packResponse();
};

// `POST /visitor/portal/documents/{id}/upload-url` — a presigned PUT, 15 minutes.
//
// THE KEY IS DERIVED SERVER-SIDE, NEVER ACCEPTED. If the browser said where it had put the file it
// could name anything. The mock returns a placeholder so the three-call shape is what the screen
// is built against, even before S3 is involved.
export const GetPortalUploadUrl_api = async (requestParams = {}) => {
  if (!USE_PORTAL_MOCKS) {
    // §6 checks the extension against the document's `accepted_file_types` BEFORE it signs
    // anything, which is why the filename goes on this call and not just the last one.
    return request(PortalDocumentUploadUrl_url(requestParams.DocumentID), {
      method: "POST",
      body: uploadUrlToApi(requestParams),
    });
  }

  await delay(MOCK_LATENCY_MS);

  const document = findDocument(requestParams.DocumentID);
  if (!document) return mockFailure(404, "That document was not found.");

  // The same envelope §6 returns, `headers` and `method` included — the browser must send those
  // headers verbatim and nothing else, or S3 refuses the signature.
  return mockResponse({
    url: "https://example.invalid/mock-presigned-put",
    headers: {},
    method: "PUT",
    expires_in: 900,
    max_bytes: 20971520,
  });
};

// `POST /visitor/portal/documents/{id}/uploaded` — records it, AFTER checking it is really there.
export const ConfirmPortalUpload_api = async (requestParams = {}) => {
  if (!USE_PORTAL_MOCKS) {
    // The server derives the bucket key from `filename` and checks the object is really there
    // before recording anything — so the browser cannot claim a file it did not put.
    return request(PortalDocumentUploaded_url(requestParams.DocumentID), {
      method: "POST",
      body: uploadedToApi(requestParams),
    });
  }

  await delay(MOCK_LATENCY_MS);

  const document = findDocument(requestParams.DocumentID);
  if (!document) return mockFailure(404, "That document was not found.");

  // NO read_only GUARD HERE — Phase C, 22-October-2026.
  //
  // This refused with 409 "This pack has been submitted and can no longer be changed",
  // which was right until Phase C and is now exactly backwards: documents stay replaceable
  // while the link is live, submitted or not. `read_only` means signatures and the
  // personnel tick, and `sign` and `personnel/confirm` still carry the guard.

  if (document.expiry_required && !requestParams.Expiry) {
    return mockFailure(422, "Enter the expiry date shown on the document.");
  }

  // `held` / `none` is the vocabulary (§3) — a flag, not a filename.
  document.file = "held";
  document.uploaded_at = new Date().toISOString().slice(0, 19).replace("T", " ");
  document.expiry = requestParams.Expiry ?? null;
  document.expiry_entered = requestParams.Expiry ?? null;

  // THE VERDICT IS USUALLY ALREADY HERE. Added 08-October-2026 from the contract update of
  // 06-October: "GuardDuty is usually finished before the portal is even told the file exists,
  // and the upload adopts the verdict from the object's tag."
  //
  // So `scan: "pending"` on this reply is the UNCOMMON case, not the normal one — the mock used
  // to return it unconditionally and could never produce a rejection, which meant the path where
  // a contractor's file is refused could not be seen in the browser at all.
  //
  // An EICAR-named file stands in for the scanner here, mirroring the contract's own test
  // procedure (§C4b). There is nothing to scan in a mock; this is the switch that lets the
  // refusal path be reviewed without a bucket.
  const looksInfected = /eicar/i.test(String(requestParams.FileName ?? ""));

  if (looksInfected) {
    document.state = "rejected";
    document.scan = "infected";
    // Deliberately flat wording that does not name what was found, and the file is KEPT —
    // §3: "held — kept on purpose, it is evidence".
    document.rejected_reason =
      "This file could not be accepted. Please upload a different copy.";
    // Back with them, so the pack cannot be submitted until it is replaced.
    document.outstanding = true;

    return packResponse();
  }

  document.state = "pending";
  document.scan = "clean";
  // NOT OUTSTANDING ANY MORE. The contract is explicit: `pending` does not count against the
  // pack, because "a screen that showed it as a to-do would have contractors uploading the same
  // file twice." They have done their part; it is with the reviewer now. Only `rejected` sends
  // it back to them, and that comes with a reason.
  document.outstanding = false;
  document.rejected_reason = null;

  // AC-P-66. The SERVER decides which sentence this is, because only it knows whether the pack
  // had already been submitted — the contractor pressed the same button either way.
  //
  // On a submitted pack a replacement goes STRAIGHT BACK to the reviewer: there is no second
  // Submit, and "Sent for review." is the whole confirmation they get.
  return packResponse(store().submitted ? "Sent for review." : "Uploaded.");
};

// `POST /visitor/portal/personnel/confirm` — step 3's tick.
export const ConfirmPortalPersonnel_api = async () => {
  if (!USE_PORTAL_MOCKS) return request(PortalPersonnelConfirm_url, { method: "POST" });

  await delay(MOCK_LATENCY_MS);

  if (store().read_only) {
    return mockFailure(409, "This pack has been submitted and can no longer be changed.");
  }

  store().steps.personnel.confirmed = true;

  return packResponse();
};

// `POST /visitor/portal/submit` — hands the pack to the reviewer.
export const SubmitPortalPack_api = async () => {
  if (!USE_PORTAL_MOCKS) return request(PortalSubmit_url, { method: "POST" });

  await delay(MOCK_LATENCY_MS);

  const pack = recount();

  if (!pack.steps.submit.can_submit) {
    return mockFailure(409, "Some items are still outstanding.");
  }

  pack.submitted = true;
  pack.submitted_at = new Date().toISOString().slice(0, 19).replace("T", " ");
  // §5: read-only after submit, a product decision of 01-October-2026.
  pack.read_only = true;
  pack.status = "review";

  return packResponse();
};

// `POST /visitor/portal/sign-out` — revokes the session.
export const SignOutPortal_api = async () => {
  if (!USE_PORTAL_MOCKS) {
    // The call goes FIRST, while the token is still attached — it is what the server revokes the
    // session by. Then it is dropped locally whatever the server said, because a sign-out that
    // leaves a live token in the tab is worse than one the server never heard about.
    const response = await request(PortalSignOut_url, { method: "POST" });

    clearPortalToken();

    return response;
  }

  clearPortalToken();

  await delay(MOCK_LATENCY_MS);

  return mockResponse({});
};

// ── The headers the browser is actually allowed to send on the PUT ───────────────────────────
//
// §6: "PUT the file straight to `url` with those headers." We follow that literally, which means
// whatever the server lists goes on the request — so this is the one place that decides what
// "those headers" can mean.
//
// Two things the server sends today that a browser cannot use:
//
//   · `Host`. A FORBIDDEN HEADER NAME — the fetch spec refuses to let script set it, and the
//     browser sets it itself from the url. Passing it through is silently dropped, so nothing
//     breaks; it is simply an instruction that can never be followed.
//   · An ARRAY value: `{"Host": ["bucket.s3.amazonaws.com"]}`. Handed to `fetch` as-is, an array
//     stringifies by joining with a comma — so two values would become the single header
//     `"a,b"`, which is not what was meant and would break a signature that covered it.
//
// Neither bites today: `X-Amz-SignedHeaders` is `host` alone and the payload is UNSIGNED, so the
// signature covers only the header the browser sets correctly by itself. This exists so that the
// day the server lists something it genuinely needs us to send — `x-amz-server-side-encryption`,
// say — it arrives as a header rather than as a comma-joined string.
//
// The list is the fetch spec's forbidden header names, trimmed to the ones a signing server might
// plausibly return. Anything else is passed through untouched: it is not our place to second
// guess what the signature covers.
//
// CONFIRMED BY THE LARAVEL TEAM, 24-October-2026, when we asked what else `headers` might carry:
//
//   "Nothing else is planned. If the bucket needs encryption, DevOps should use the bucket's
//    default encryption (SSE-S3 or SSE-KMS), which needs no header from the client. If we ever
//    do require one (for example x-amz-server-side-encryption), it will appear in headers as a
//    string and we'll put a note on the ticket first. Their client-side filter should drop only
//    the browser's forbidden names and send everything else."
//
// Which is what this does. Two consequences worth keeping in mind:
//
//   · The LIST is deliberately short rather than exhaustive. A forbidden name that is not on it
//     is dropped by the BROWSER anyway — the fetch spec enforces this, not us — so a gap here
//     costs nothing. This list exists to make the intent readable, not to police the browser.
//   · The real work is the ARRAY FLATTENING. Values will be strings from now on, so that half is
//     defensive only — but it is the half that would have silently mangled a signed header into
//     "a,b", and nothing downstream would have said so.
const FORBIDDEN_HEADERS = new Set([
  "host",
  "connection",
  "content-length",
  "origin",
  "referer",
  "date",
  "cookie",
  "transfer-encoding",
  "upgrade",
  "via",
]);

export const sendableHeaders = (headers) => {
  if (!headers || typeof headers !== "object") return {};

  return Object.entries(headers).reduce((out, [name, value]) => {
    if (FORBIDDEN_HEADERS.has(String(name).toLowerCase())) return out;

    // One value per header. An array with more than one entry is ambiguous — joining them is a
    // guess — so the first is taken and the rest dropped rather than silently concatenated.
    const single = Array.isArray(value) ? value[0] : value;

    if (single === undefined || single === null) return out;

    out[name] = String(single);

    return out;
  }, {});
};

// ── Mapped wrappers ──────────────────────────────────────────────────────────────
//
// THE ONLY THING SCREENS SHOULD CALL. Each unwraps the envelope and runs the payload through
// `portalApiMapper`, so a screen sees one flat shape whichever path answered — and the mapper runs
// on the fixtures too, which is the whole reason they are in wire format.
export const fetchPortalPack = async () => {
  const response = await GetPortalPack_api();

  if (!isSuccessfulResponse(response)) return { ok: false, response, pack: null };

  return { ok: true, response, pack: packFromApi(response.Data.Records) };
};

export const requestPortalCode = async (params) => {
  const response = await RequestPortalCode_api(params);

  if (!isSuccessfulResponse(response)) return { ok: false, response, sent: false };

  return { ok: true, response, ...requestCodeFromApi(response.Data.Records) };
};

// The first call. A 404 is a REFUSAL, not an error — an unknown token, or a live link to a pack
// that has already finished — so the caller gets `ok: false` with the response to read the
// server's own sentence from, exactly like every other refusal in this module.
export const openPortal = async () => {
  const response = await OpenPortal_api();

  if (!isSuccessfulResponse(response)) return { ok: false, response, open: null };

  return { ok: true, response, open: openFromApi(response.Data.Records) };
};

export const verifyPortalCode = async (params) => {
  const response = await VerifyPortalCode_api(params);

  if (!isSuccessfulResponse(response)) return { ok: false, response, token: null };

  const mapped = verifyCodeFromApi(response.Data.Records);
  // Held for the life of the tab and nowhere else — see `setPortalToken`.
  setPortalToken(mapped.token);

  return { ok: true, response, ...mapped };
};

export const fetchPortalDocument = async (params) => {
  const response = await GetPortalDocument_api(params);

  if (!isSuccessfulResponse(response)) return { ok: false, response, document: null };

  return { ok: true, response, document: documentBodyFromApi(response.Data.Records) };
};

// Every write answers with the WHOLE pack (§4), so they all return it the same way and callers
// replace their state wholesale rather than patching the row they touched.
const writeReturningPack = async (call) => {
  const response = await call();

  if (!isSuccessfulResponse(response)) return { ok: false, response, pack: null };

  return { ok: true, response, pack: packFromApi(response.Data.Records) };
};

export const signPortalDocument = (params) =>
  writeReturningPack(() => SignPortalDocument_api(params));

export const confirmPortalPersonnel = () => writeReturningPack(ConfirmPortalPersonnel_api);

export const submitPortalPack = () => writeReturningPack(SubmitPortalPack_api);

// The three-call upload (§4): ask for a URL, PUT the bytes, then tell the server it landed. If the
// last call is missed the file sits in the bucket and the document still reads `awaiting_upload` —
// correct, because the server does not record a file it has not confirmed is there.
export const uploadPortalDocument = async ({ DocumentID, File: file, Expiry }) => {
  // The filename goes on the FIRST call too: §6 checks its extension against the document's
  // `accepted_file_types` before it signs anything, so a `.exe` is refused here with a sentence
  // the contractor can act on rather than failing silently at the bucket.
  const urlResponse = await GetPortalUploadUrl_api({ DocumentID, FileName: file?.name });

  if (!isSuccessfulResponse(urlResponse)) return { ok: false, response: urlResponse, pack: null };

  // Step two is a PUT straight to S3 — the bytes never pass through the API. Skipped while
  // mocked; there is no bucket, and inventing one would test nothing.
  if (!USE_PORTAL_MOCKS) {
    const { url, headers, method } = urlResponse.Data.Records;

    // SEND EXACTLY THE HEADERS THE SERVER LISTED, AND NOTHING ELSE. The signature covers them, so
    // an extra one — Authorization above all — makes S3 reject the PUT. We used to send none and
    // hardcode the verb, which would have broken the moment the server started listing any.
    const putResponse = await fetch(url, {
      method: method || "PUT",
      headers: sendableHeaders(headers),
      body: file,
    });

    // A failed PUT must not be followed by the third call. The server would answer 409 "We could
    // not find your uploaded file", but saying so here keeps the real reason — the bucket refused
    // — instead of reporting it as a missing file.
    if (!putResponse.ok) {
      return {
        ok: false,
        response: mockFailure(409, "We could not upload your file. Please try again."),
        pack: null,
      };
    }
  }

  return writeReturningPack(() =>
    ConfirmPortalUpload_api({ DocumentID, Expiry, FileName: file?.name })
  );
};
